import './BuyTokensModal.css'
import { container } from 'tsyringe'
import { ApiService } from '../api/ApiService'
import { useState } from 'react'
import { useTelegram } from '../hooks/useTelegram'
import { BodyBlocker } from './BodyBlocker'
import { NotifyModal } from './NotifyModal'
import { useTranslation } from 'react-i18next'

const PACKS = [1, 50, 100, 500, 1000, 10000]
const PREMIUMS_LVL1 = [
  { label: '1 месяц (Stars)', type: 'TelegramStars', value: 1, amount: 350 },
  { label: '1 месяц (TON)', type: 'CryptoPay', value: 1, amount: 7 },
  { label: '6 месяцев (Stars)', type: 'TelegramStars', value: 6, amount: 2000 },
  { label: '6 месяцев (TON)', type: 'CryptoPay', value: 6, amount: 42 },
  { label: '1 год (Stars)', type: 'TelegramStars', value: 12, amount: 4000 },
  { label: '1 год (TON)', type: 'CryptoPay', value: 12, amount: 84 },
]
const PREMIUMS_LVL2 = [
  { label: '1 месяц (Stars)', type: 'TelegramStars', value: 1, amount: 750 },
  { label: '1 месяц (TON)', type: 'CryptoPay', value: 1, amount: 14 },
  { label: '6 месяцев (Stars)', type: 'TelegramStars', value: 6, amount: 4500 },
  { label: '6 месяцев (TON)', type: 'CryptoPay', value: 6, amount: 84 },
  { label: '1 год (Stars)', type: 'TelegramStars', value: 12, amount: 9000 },
  { label: '1 год (TON)', type: 'CryptoPay', value: 12, amount: 168 },
]

export function BuyTokensModal({
  onClose,
  onPremiumChanged,
  currentPremiumUntil,
}: {
  onClose: () => void
  onPremiumChanged?: (info: { premiumLevel: number; premiumUntil: string }) => void
  currentPremiumUntil?: string | null
}) {
  const api = container.resolve(ApiService)
  const { tg } = useTelegram()
  const { t } = useTranslation()
  const [processing, setProcessing] = useState<string | null>(null)
  const [selectedStars, setSelectedStars] = useState<number>(100)
  const [selectedTon, setSelectedTon] = useState<number>(100)
  const [selectedPremium1, setSelectedPremium1] = useState(PREMIUMS_LVL1[0].label)
  const [selectedPremium2, setSelectedPremium2] = useState(PREMIUMS_LVL2[0].label)
  const [notifyText, setNotifyText] = useState<string | null>(null)

  const openExternalInvoiceLink = (url: string) => {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url)
      return
    }

    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleBuyStars = async () => {
    if (processing) return
    const key = `stars-${selectedStars}`
    setProcessing(key)
    try {
      const { invoiceLink } = await api.requestInvoice(selectedStars, 'TelegramStars')
      tg?.openInvoice?.(invoiceLink)
    } catch (err) {
      console.error('Failed to buy tokens', err)
    } finally {
      setTimeout(() => setProcessing(null), 1000)
    }
  }

  const handleBuyTon = async () => {
    if (processing) return
    const key = `ton-${selectedTon}`
    setProcessing(key)
    try {
      const { invoiceLink } = await api.requestInvoice(selectedTon, 'CryptoPay')
      openExternalInvoiceLink(invoiceLink)
    } catch (err) {
      console.error('Failed to buy tokens via TON', err)
    } finally {
      setTimeout(() => setProcessing(null), 1000)
    }
  }

  const handleBuyPremium = async (level: 'Premium1' | 'Premium2', selectedLabel: string) => {
    if (processing) return
    const source = level === 'Premium1' ? PREMIUMS_LVL1 : PREMIUMS_LVL2
    const option = source.find(p => p.label === selectedLabel)
    if (!option) return
    const key = `${level}-${option.label}`
    setProcessing(key)

    try {
      const { invoiceLink } = await api.requestPremiumInvoice({
        provider: option.type,
        amount: option.amount,
        purpose: level
      })

      const confirm = async (payload?: { status?: string }) => {
        if (payload?.status === 'paid') {
          await api.confirmPremium({
            provider: option.type,
            amount: option.amount,
            purpose: level
          })

          const start =
            currentPremiumUntil && new Date(currentPremiumUntil) > new Date()
              ? new Date(currentPremiumUntil)
              : new Date()
          start.setMonth(start.getMonth() + option.value)
          const premiumUntil = start.toISOString()
          onPremiumChanged?.({ premiumLevel: level === 'Premium1' ? 1 : 2, premiumUntil })
          setNotifyText(t('purchase_success'))
        }
        tg?.offEvent?.('invoiceClosed', confirm)
      }

      tg?.onEvent?.('invoiceClosed', confirm)

      if (option.type === 'TelegramStars') {
        tg?.openInvoice?.(invoiceLink)
      } else {
        openExternalInvoiceLink(invoiceLink)
      }
    } catch (err) {
      console.error('Failed to buy premium1', err)
    } finally {
      setTimeout(() => setProcessing(null), 1000)
    }
  }
  return (
    <div className="buytokens-backdrop" onClick={onClose}>
      <div className="buytokens-content" onClick={e => e.stopPropagation()}>
        <BodyBlocker />
        <div className="buytokens-banner">
          <span>{t('buy_premium_desc')}</span>
        </div>
        <div className="buytokens-section">
          <h3>{t('gleam_premium')}</h3>
          <p>{t('premium_desc')}</p>
          <div className="buytokens-row">
            <div className="buytokens-select">
              <select value={selectedPremium1} onChange={e => setSelectedPremium1(e.target.value)}>
                {PREMIUMS_LVL1.map(p => (
                  <option key={p.label} value={p.label}>{p.label}</option>
                ))}
              </select>
            </div>
            <button
              className="buytokens-btn"
              onClick={() => handleBuyPremium('Premium1', selectedPremium1)}
              disabled={!!processing}
            >
              {processing?.startsWith('Premium1') ? t('loading') : t('buy')}
            </button>
          </div>
        </div>

        <div className="buytokens-section">
          <h3>{t('gleam_x')}</h3>
          <p>{t('gleam_x_desc')}</p>
          <div className="buytokens-row">
            <div className="buytokens-select">
              <select value={selectedPremium2} onChange={e => setSelectedPremium2(e.target.value)}>
                {PREMIUMS_LVL2.map(p => (
                  <option key={p.label} value={p.label}>{p.label}</option>
                ))}
              </select>
            </div>
            <button
              className="buytokens-btn"
              onClick={() => handleBuyPremium('Premium2', selectedPremium2)}
              disabled={!!processing}
            >
              {processing?.startsWith('Premium2') ? t('loading') : t('buy')}
            </button>
          </div>
        </div>

        <div className="buytokens-section">
          <h3>{t('tokens')}</h3>
          <p>{t('tokens_desc')}</p>
          <div className="buytokens-row">
            <div className="buytokens-select">
              <select value={selectedStars} onChange={e => setSelectedStars(+e.target.value)}>
                {PACKS.map(p => <option key={`stars-${p}`} value={p}>{p}</option>)}
              </select>
            </div>
            <button
              className="buytokens-btn"
              onClick={handleBuyStars}
              disabled={!!processing}
            >
              {processing?.startsWith('stars') ? t('loading') : 'Stars'}
            </button>
          </div>
          <div className="buytokens-row">
            <div className="buytokens-select">
              <select value={selectedTon} onChange={e => setSelectedTon(+e.target.value)}>
                {PACKS.map(p => <option key={`ton-${p}`} value={p}>{p}</option>)}
              </select>
            </div>
            <button
              className="buytokens-btn"
              onClick={handleBuyTon}
              disabled={!!processing}
            >
              {processing?.startsWith('ton') ? t('loading') : 'TON'}
            </button>
          </div>
        </div>

        <div className="buytokens-actions">
          <button className="buytokens-back" onPointerUp={onClose}>{t('back')}</button>
        </div>
      </div>
      {notifyText && (
        <NotifyModal
          text={notifyText}
          onClose={() => setNotifyText(null)}
        />
      )}
    </div>
  )
}
